import {
  styled,
  Grid,
  TableCell,
  Button,
  tableCellClasses,
  Typography,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PageHeader from '../../../../components/PageHeader';
import { api } from '../../../../api';

import { User } from '../../../../schema/user';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form } from '../../SystemManagement';
import CompanyFromInput from '../../../../components/formInput/CompanyFromInput';
import UserRoleFromInput from '../../../../components/formInput/UserRoleFromInput';

const UserEdit: React.FC = () => {
  const { user_id } = useParams();
  const navigate = useNavigate();

  const [username, setUsername] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);

  const [formError, setFormError] = useState<Record<string, string>>({});

  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadData = () => {
    if (!user_id) {
      return;
    }
    api.get<User>(`/user/${user_id}`).then((res) => {
      const { data } = res;
      setUsername(data.user_name);
      setFirstName(data.first_name ? data.first_name : '');
      setLastName(data.last_name ? data.last_name : '');
      setEmail(data.email ? data.email : '');
      setPhoneNumber(data.phone_number ? data.phone_number : '');
      setRoleId(data.role_id ? data.role_id : null);
      setCompanyId(data.company_id ? data.company_id : null);
    });
  };

  const openSuccessDialog = () => {
    setIsOpenSuccessDialog(true);
    setTimeout(() => {
      setIsOpenSuccessDialog(false);
      navigate('/user');
    }, 1000);
  };

  const validateInput = () => {
    const fErr: Record<string, string> = {};

    if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      fErr['email'] = 'Invalid email address';
    }

    if (Object.keys(fErr).length !== 0) {
      setFormError(fErr);
      return false;
    }
    setFormError({});
    return true;
  };
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateInput()) {
      return;
    }
    save();
  };

  const save = () => {
    if (isLoading) {
      return;
    }
    if (user_id) {
      if (!validateInput()) {
        return;
      }
      setIsLoading(true);
      api
        .put(`/user/${user_id}`, {
          user_name: username,
          first_name: firstName,
          last_name: lastName,
          email: email ? email : undefined,
          phone_number: phoneNumber,
          role_id: roleId,
          company_id: companyId
        })
        .then(() => {
          openSuccessDialog();
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Container>
      <PageHeader text="แก้ไขข้อมูล User" />
      <Button component={Link} to={`/user`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        User
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">{'ข้อมูล User'}</Typography>
        <Form onSubmit={handleFormSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                placeholder="Username"
                value={username ? username : ''}
                InputProps={{ readOnly: true }}
                disabled={true}
                sx={{
                  '.Mui-disabled': {
                    color: '#65748B',
                    opacity: 1,
                    '-webkit-text-fill-color': '#65748B'
                  },
                  '.MuiInputLabel-root': {
                    color: '#65748B',
                    '-webkit-text-fill-color': '#65748B'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <UserRoleFromInput value={roleId} onChange={(id) => setRoleId(id)} required />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={3}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ชื่อ"
                variant="outlined"
                placeholder="ชื่อ"
                onChange={(e) => setFirstName(e.target.value ? e.target.value : '')}
                value={firstName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="นามสกุล"
                variant="outlined"
                placeholder="นามสกุล"
                onChange={(e) => setLastName(e.target.value ? e.target.value : '')}
                value={lastName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="อีเมล"
                variant="outlined"
                placeholder="อีเมล"
                onChange={(e) => setEmail(e.target.value ? e.target.value : '')}
                value={email}
                error={!!formError['email']}
                helperText={formError['email']}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="เบอร์โทรศัพท์"
                variant="outlined"
                placeholder="เบอร์โทรศัพท์"
                onChange={(e) => setPhoneNumber(e.target.value ? e.target.value : '')}
                value={phoneNumber}
              />
            </Grid>
            <Grid item xs={6}>
              <CompanyFromInput value={companyId} onChange={(id) => setCompanyId(id)} />
            </Grid>
            <Grid container columns={2} spacing={2} sx={{ mt: 0 }} direction={'row-reverse'}>
              <Grid item>
                <Button type="submit" variant="contained" color="primary">
                  บันทึก
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </Paper>
      <Dialog open={isOpenSuccessDialog} onClose={() => setIsOpenSuccessDialog(false)}>
        <DialogTitle>{'Success'}</DialogTitle>
        <DialogContent>
          <DialogContentText>ดำเนินการสำเร็จ</DialogContentText>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default UserEdit;

const Container = styled('div')`
  padding: 24px;
`;
